import { useMemo, useState } from 'react';
import Modal from './Modal';
import { PersonMultiPicker, PersonPicker } from './PersonPicker';
import {
  ACTION_PRIORITY_LABELS,
  ACTION_PRIORITY_ORDER,
  type ActionPriority,
  type PersonOption,
  type Goal,
  type Initiative,
  type NamedRecord,
  type Unit,
} from '../types';

export interface ActionDraft {
  title: string;
  description: string;
  districtId: string;
  priority: ActionPriority;
  dueDate: string | null;
  /** The owner is a PERSON (any live person in the district who may be assigned), not a roster Contact. */
  ownerPersonId: string;
  participantPersonIds: string[];
  relatedUnitId: string | null;
  relatedPersonId: string | null;
  relatedGoalId: string | null;
  relatedInitiativeId: string | null;
  subcommitteeId: string | null;
}

/** Manual Action creation — deliberately minimal: Title + Owner are the
 *  only required fields, everything else collapses into a plain optional
 *  section below rather than looking like a project-management form. */
export default function ActionForm({
  districts,
  people,
  labelFor,
  onAddPerson,
  units,
  goals,
  initiatives,
  subcommittees,
  defaultDistrictId,
  onClose,
  onSave,
}: {
  districts: NamedRecord[];
  people: PersonOption[];
  labelFor: (personId: string) => string | null;
  /** Opens the normal Add Person flow (duplicate check first); never creates a bare record. */
  onAddPerson?: () => void;
  units: Unit[];
  goals: Goal[];
  initiatives: Initiative[];
  subcommittees: NamedRecord[];
  defaultDistrictId: string;
  onClose: () => void;
  onSave: (draft: ActionDraft) => Promise<void>;
}) {
  const [d, setD] = useState<ActionDraft>({
    title: '',
    description: '',
    districtId: defaultDistrictId || districts[0]?.id || '',
    priority: 'normal',
    dueDate: null,
    ownerPersonId: '',
    participantPersonIds: [],
    relatedUnitId: null,
    relatedPersonId: null,
    relatedGoalId: null,
    relatedInitiativeId: null,
    subcommitteeId: null,
  });
  const [showMore, setShowMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const set = <K extends keyof ActionDraft>(k: K, v: ActionDraft[K]) =>
    setD((prev) => ({ ...prev, [k]: v }));

  const districtUnits = useMemo(
    () => units.filter((u) => u.districtId === d.districtId).sort((a, b) => a.name.localeCompare(b.name)),
    [units, d.districtId],
  );
  const districtInitiatives = useMemo(
    () =>
      initiatives.filter((i) => i.districtId === d.districtId).sort((a, b) => a.name.localeCompare(b.name)),
    [initiatives, d.districtId],
  );

  return (
    <Modal title="New Action" onClose={onClose}>
      <label>Title</label>
      <input
        value={d.title}
        onChange={(e) => set('title', e.target.value)}
        placeholder="What needs to happen?"
        autoFocus
      />

      <div style={{ display: 'flex', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <label>District</label>
          <select
            value={d.districtId}
            onChange={(e) =>
              setD((prev) => ({
                ...prev,
                districtId: e.target.value,
                ownerPersonId: '',
                participantPersonIds: [],
                relatedUnitId: null,
                relatedInitiativeId: null,
                subcommitteeId: null,
              }))
            }
          >
            {districts.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
          </select>
        </div>
        <div style={{ flex: 1 }}>
          <label>Owner</label>
          <PersonPicker
            options={people}
            districtId={d.districtId}
            value={d.ownerPersonId}
            onChange={(id) => set('ownerPersonId', id)}
            labelFor={labelFor}
            onAddPerson={onAddPerson}
            placeholder="Search for the owner…"
          />
        </div>
      </div>

      {!showMore && (
        <button type="button" className="btn" style={{ marginTop: 8 }} onClick={() => setShowMore(true)}>
          + Add description, due date, participants, related records
        </button>
      )}

      {showMore && (
        <>
          <label>Description</label>
          <textarea rows={2} value={d.description} onChange={(e) => set('description', e.target.value)} />

          {subcommittees.length > 0 && <>
            <label>Responsible Subcommittee (optional)</label>
            <select value={d.subcommitteeId ?? ''} onChange={(e) => set('subcommitteeId', e.target.value || null)}>
              <option value="">— none —</option>
              {subcommittees.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </>}

          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label>Priority</label>
              <select value={d.priority} onChange={(e) => set('priority', e.target.value as ActionPriority)}>
                {ACTION_PRIORITY_ORDER.map((p) => (
                  <option key={p} value={p}>
                    {ACTION_PRIORITY_LABELS[p]}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label>Due date</label>
              <input
                type="date"
                value={d.dueDate ?? ''}
                onChange={(e) => set('dueDate', e.target.value || null)}
              />
            </div>
          </div>

          <label>Participants (optional)</label>
          <PersonMultiPicker
            options={people}
            districtId={d.districtId}
            values={d.participantPersonIds}
            exclude={d.ownerPersonId ? [d.ownerPersonId] : []}
            onChange={(ids) => set('participantPersonIds', ids)}
            labelFor={labelFor}
            onAddPerson={onAddPerson}
          />

          <label>Related person (optional)</label>
          <PersonPicker
            options={people}
            districtId={d.districtId}
            value={d.relatedPersonId ?? ''}
            onChange={(id) => set('relatedPersonId', id || null)}
            labelFor={labelFor}
            placeholder="Search for a person…"
          />

          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label>Related unit (optional)</label>
              <select
                value={d.relatedUnitId ?? ''}
                onChange={(e) => set('relatedUnitId', e.target.value || null)}
              >
                <option value="">— none —</option>
                {districtUnits.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label>Related initiative (optional)</label>
              <select
                value={d.relatedInitiativeId ?? ''}
                onChange={(e) => set('relatedInitiativeId', e.target.value || null)}
              >
                <option value="">— none —</option>
                {districtInitiatives.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <label>Related goal (optional)</label>
          <select value={d.relatedGoalId ?? ''} onChange={(e) => set('relatedGoalId', e.target.value || null)}>
            <option value="">— none —</option>
            {goals
              .filter((g) => g.status === 'active')
              .map((g) => (
                <option key={g.id} value={g.id}>
                  {g.title}
                </option>
              ))}
          </select>
        </>
      )}

      {err && <div className="err">{err}</div>}

      <div className="actions">
        <button className="btn" onClick={onClose}>
          Cancel
        </button>
        <button
          className="btn primary"
          disabled={busy || !d.title.trim() || !d.ownerPersonId}
          onClick={async () => {
            setBusy(true);
            setErr(null);
            try {
              await onSave({ ...d, title: d.title.trim(), description: d.description.trim() });
            } catch (e) {
              setErr(e instanceof Error ? e.message : 'Could not create that Action.');
            } finally {
              setBusy(false);
            }
          }}
        >
          Create Action
        </button>
      </div>
    </Modal>
  );
}
